// Check and enforce swatch product quantities in cart:
// - at most 1 per variant
// - at most `max_qty_one_product_total` across all variants (e.g. 8 swatches per order)
// in component-cart-item.liquid we are also disabling the quantity selectors for swatch products.
// in theme.liquid we add this script and pass the config from settings_schema.json to this CONFIG object.
// window.SwatchLimit is the shared helper used by the swatch ordering store and the Cart.addToCart guard.
(function () {
  'use strict';

  const settings = window.themeSettings || {};

  const CONFIG = {
    maxOneProductHandle: settings.max_qty_one_product || null,
    variantIds: new Set((settings.max_qty_one_product_variant_ids || []).map(Number)),
    maxTotal: Number(settings.max_qty_one_product_total) || 0,
    limitMessage: settings.max_qty_one_product_limit_message || '',
  };

  let enforcementRunning = false;
  let isEnforcementUpdate = false;

  function isMaxOneProduct(item) {
    if (!CONFIG.maxOneProductHandle) return false;
    return item.handle === CONFIG.maxOneProductHandle || CONFIG.variantIds.has(Number(item.variant_id));
  }

  const SwatchLimit = {
    max: CONFIG.maxOneProductHandle ? CONFIG.maxTotal : 0,
    count: Number(settings.max_qty_one_product_in_cart) || 0,
    message: CONFIG.limitMessage,

    isSwatchVariant(id) {
      return CONFIG.variantIds.has(Number(id));
    },

    countIn(cart) {
      return (cart?.items || []).filter(isMaxOneProduct).reduce((sum, item) => sum + item.quantity, 0);
    },

    remaining() {
      if (!this.max) return Infinity;
      return Math.max(0, this.max - this.count);
    },

    setFromCart(cart) {
      this.count = this.countIn(cart);
      window.dispatchEvent(new CustomEvent('swatch-limit-change', { detail: { count: this.count } }));
    },

    async refresh() {
      try {
        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { cache: 'no-store' });
        if (response.ok) this.setFromCart(await response.json());
      } catch (error) {
        console.error('Error reading cart for swatch limit:', error);
      }
      return this.count;
    },
  };

  window.SwatchLimit = SwatchLimit;

  // Number of swatch units an add-to-cart FormData would add (supports `id` and `items[n][id]` payloads).
  function swatchQtyInFormData(data) {
    if (!(data instanceof FormData)) return 0;

    let qty = 0;
    const id = data.get('id');
    if (id && SwatchLimit.isSwatchVariant(id)) {
      qty += Number(data.get('quantity')) || 1;
    }

    const items = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (!match) continue;
      items[match[1]] = items[match[1]] || {};
      items[match[1]][match[2]] = value;
    }
    Object.values(items).forEach((item) => {
      if (item.id && SwatchLimit.isSwatchVariant(item.id)) qty += Number(item.quantity) || 1;
    });

    return qty;
  }

  function guardAddToCart() {
    const cart = window.Cart;
    if (!cart || typeof cart.addToCart !== 'function' || cart.addToCart._swatchGuarded) return;

    const originalAddToCart = cart.addToCart;
    const guardedAddToCart = async function (data, ...rest) {
      const swatchQty = swatchQtyInFormData(data);
      if (swatchQty > 0 && SwatchLimit.max) {
        await SwatchLimit.refresh();
        if (swatchQty > SwatchLimit.remaining()) {
          const error = new Error(SwatchLimit.message || 'Swatch limit reached');
          error.description = SwatchLimit.message;
          window.dispatchEvent(new CustomEvent('swatch-limit-exceeded', { detail: { message: SwatchLimit.message } }));
          throw error;
        }
      }
      return originalAddToCart.call(this, data, ...rest);
    };
    guardedAddToCart._swatchGuarded = true;
    cart.addToCart = guardedAddToCart;
  }

  async function updateItemQuantity(key, quantity) {
    try {
      isEnforcementUpdate = true;
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      if (!response.ok) throw new Error('Failed to update cart');
      const result = await response.json();
      isEnforcementUpdate = false;
      return result;
    } catch (error) {
      console.error('Error updating max qty product:', error);
      isEnforcementUpdate = false;
      throw error;
    }
  }

  // Cart items are newest first, so the most recently added swatches are the ones trimmed when over the total.
  // The same variant can sit on several lines (e.g. different line properties), so only its oldest line is kept.
  function getQuantityFixes(cart) {
    const swatchItems = cart.items.filter(isMaxOneProduct);

    const keptKeys = new Set();
    const keptVariants = new Set();
    for (const item of [...swatchItems].reverse()) {
      if (item.quantity > 0 && !keptVariants.has(item.variant_id)) {
        keptVariants.add(item.variant_id);
        keptKeys.add(item.key);
      }
    }

    const allowedTotal = SwatchLimit.max || Infinity;
    let excess = keptKeys.size - allowedTotal;
    let trimmedForTotal = false;

    const fixes = [];
    for (const item of swatchItems) {
      let target = keptKeys.has(item.key) ? Math.min(item.quantity, 1) : 0;
      if (excess > 0 && target > 0) {
        const cut = Math.min(excess, target);
        target -= cut;
        excess -= cut;
        trimmedForTotal = true;
      }
      if (target !== item.quantity) fixes.push({ key: item.key, quantity: target });
    }

    return { fixes, trimmedForTotal };
  }

  async function enforceMaxOneQuantities(depth = 0) {
    if (enforcementRunning || depth > 3 || !CONFIG.maxOneProductHandle) return;

    enforcementRunning = true;

    try {
      const response = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await response.json();
      SwatchLimit.setFromCart(cart);

      const { fixes, trimmedForTotal } = getQuantityFixes(cart);

      if (fixes.length === 0) {
        if (depth > 0 && window.Cart?.updateCart) {
          await window.Cart.updateCart(false);
        }
        enforcementRunning = false;
        return;
      }

      for (const fix of fixes) {
        await updateItemQuantity(fix.key, fix.quantity);
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      if (trimmedForTotal) {
        window.dispatchEvent(new CustomEvent('swatch-limit-exceeded', { detail: { message: SwatchLimit.message } }));
      }

      enforcementRunning = false;

      await new Promise(resolve => setTimeout(resolve, 300));
      await enforceMaxOneQuantities(depth + 1);

    } catch (error) {
      console.error('Error enforcing max one quantities:', error);
      enforcementRunning = false;
    }
  }

  function hookFetch() {
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
      const response = await originalFetch.apply(this, args);
      try {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;

        if (url && /\/cart\/(add|change|update)\.js/i.test(url) && !isEnforcementUpdate) {
          const clone = response.clone();
          clone.json().then(async () => {
            await new Promise(resolve => setTimeout(resolve, 500));
            enforceMaxOneQuantities();
          }).catch(() => {});
        }
      } catch (_) {}
      return response;
    };
  }

  function init() {
    if (!CONFIG.maxOneProductHandle) {
    // console.log('No max quantity product configured');
      return;
    }

    hookFetch();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        guardAddToCart();
        setTimeout(enforceMaxOneQuantities, 1000);
      });
    } else {
      guardAddToCart();
      setTimeout(enforceMaxOneQuantities, 1000);
    }
  }

  init();
})();
